import { Injectable } from '@angular/core';
import { Model } from '../../model/model';
import { DataStore, isDataStoreAutoincrement } from '../../util/data-store';
import { AppConfig } from '../../util/app-config';

@Injectable()
export class RepositoryProvider {

	private _db: IDBDatabase;

	upsert(store: DataStore, obj: Model): Promise<Model> {
		return this.getStore(store, false)
			.then(store => {
				return new Promise<Model>(resolve => {
					const request = store.put(obj);

					request.onsuccess = event => {
						resolve((<Model>(<any>event.target).result));
					};

					request.onerror = event => {
						const target: any = event.target;
						const msg = target.result;
						console.log(msg);
						resolve(null);
					};
				})
			})
			.catch(e => {
				return new Promise<Model>(resolve => {
					console.log(e);
					resolve(null);
				});
			});
	}

	delete(store: DataStore, key: string): Promise<void> {
		return this.getStore(store, false)
			.then(store => {
				return new Promise<void>(resolve => {
					const request = store.delete(key);

					request.onsuccess = event => {
						resolve();
					};

					request.onerror = event => {
						const target: any = event.target;
						const msg = target.result;
						console.log(msg);
						resolve();
					};
				})
			})
			.catch(e => {
				return new Promise<void>(resolve => {
					console.log(e);
					resolve();
				});
			});
	}

	clearStore(store: DataStore): void {
		this.getStore(store, false)
			.then(store => {
				store.clear();
			})
			.catch(e => {
				console.log(e);
				throw new Error(e);
			});
	}

	async clearAllStore() {
		await Object.keys(DataStore).map(async (k) => {
			await this.clearStore(DataStore[k]);
		});
	}

	findOne(store: DataStore, id: string): Promise<any> {
		return this.getStore(store, true)
			.then(store => {
				return new Promise<any>(resolve => {
					const request = store.get(id);

					request.onsuccess = event => {
						resolve((<any>(<any>event.target).result));
					};

					request.onerror = event => {
						const target: any = event.target;
						const msg = target.result;
						console.log(msg);
						resolve(null);
					};
				});
			});
	}

	findAll(store: DataStore): Promise<any[]> {
		return this.getStore(store, true)
			.then(store => {
				return new Promise<any[]>(resolve => {
					const request = store.getAll();

					request.onsuccess = event => {
						resolve((<any[]>(<any>event.target).result));
					};

					request.onerror = event => {
						const target: any = event.target;
						const msg = target.result;
						console.log(msg);
						resolve([]);
					};




				});
			});
	}

	findAllWithOrder(store: DataStore, order: Function): Promise<any[]> {
		return this.getStore(store, true)
			.then(store => {
				return new Promise<any[]>(resolve => {
					const request = store.getAll();

					request.onsuccess = event => {
						resolve(<any[]>(<any>event.target).result.sort(order));
					};

					request.onerror = event => {
						const target: any = event.target;
						const msg = target.result;
						console.log(msg);
						resolve([]);
					};
				});
			});
	}

	findAllWithReduce(store: DataStore, reduce: Function): Promise<any[]> {
		return this.getStore(store, true)
			.then(store => {
				return new Promise<any[]>(resolve => {
					const request = store.getAll();

					request.onsuccess = event => {
						resolve((<any[]>(<any>event.target).result)
							.map((obj) => reduce.apply(this, [obj]))
							.reduce((obj1, obj2) => obj1.concat(obj2), []));
					};

					request.onerror = event => {
						const target: any = event.target;
						const msg = target.result;
						console.log(msg);
						resolve([]);
					};
				});
			});
	}

	findBy(store: DataStore, filter: Function): Promise<Model[]> {
		return this.getStore(store, true)
			.then(store => {
				return new Promise<Model[]>(resolve => {
					const request = store.getAll();

					request.onsuccess = event => {
						let models = (<Model[]>(<any>event.target).result);
						resolve(models.filter(model => filter.apply(this, [model])));
					};

					request.onerror = event => {
						const target: any = event.target;
						const msg = target.result;
						console.log(msg);
						resolve([]);
					}
				});
			});
	}

	private getStore(store: DataStore, readonly: boolean): Promise<IDBObjectStore> {
		return this.initDb(store)
			.then(() => {
				const transactionMode = readonly ? 'readonly' : 'readwrite';
				const tx = this._db.transaction(store, transactionMode);
				return tx.objectStore(store);
			})
			.catch(e => {
				console.log(e);
				return null;
			});
	}

	private initDb(store: DataStore): Promise<void> {
		this.closeDB();

		return new Promise(resolve => {
			const openRequest = indexedDB.open(AppConfig.DB_NAME, AppConfig.DB_VERSION);

			openRequest.onupgradeneeded = event => {
				const target: any = event.target;
				const db = target.result;
				Object.keys(DataStore)
					.map(key => DataStore[key])
					.forEach(dataStore => {
						db.createObjectStore(dataStore, {
							keyPath: '_id',
							autoIncrement: isDataStoreAutoincrement(dataStore)
						});
					});

				resolve();
			};

			openRequest.onerror = event => {
				const target: any = event.target;
				const msg = target.result;
				console.log(msg);
				resolve();
			};

			openRequest.onsuccess = event => {
				this._db = (<any>event.target).result;
				this._db.onerror = event => {
					console.log(event);
					throw Error((<any>event.target).result);
				};
				resolve();
			};
		});
	}

	private closeDB() {
		if (this._db) {
			this._db.close();
		}
	}

}
